import { AsyncPipe, CommonModule, NgForOf, NgIf } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { TuiDay, TuiDayRange, TuiValidationError } from "@taiga-ui/cdk";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDropdown,
  TuiError,
  TuiLink,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBreadcrumbs,
  TuiDataListWrapper,
  TuiFade,
  TuiFieldErrorPipe,
  TuiFiles,
  TuiStringifyContentPipe,
  TuiTabs,
  TuiUnfinishedValidator,
} from "@taiga-ui/kit";
import { TuiCell, TuiNavigation } from "@taiga-ui/layout";
import {
  TuiComboBoxModule,
  TuiInputDateModule,
  TuiInputDateRangeModule,
  TuiInputDateTimeModule,
  TuiInputModule,
  TuiInputPhoneModule,
  TuiInputTimeModule,
  TuiMultiSelectModule,
  TuiSelectModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { catchError, of, switchMap, throwError } from "rxjs";
import { ClientService } from "../../core/services/client.service";
import { TuiTable } from "@taiga-ui/addon-table";
import { InvoiceService } from "../../core/services/invoice.service";
import { NurseService } from "../../core/services/nurse.service";
import { SalaryService } from "../../core/services/salary.service";
import { ShiftService } from "../../core/services/shift.service";
import { ChargesService } from "../../core/services/charges.service";
import { SalaryConfigService } from "../../core/services/salary-config.service";
import { ChargeConfigService } from "../../core/services/charge-config.service";
import { DisableUnlessPermissionDirective } from "../../core/directives/disable-unless-permission.directive";
import { EnterSubmitDirective } from "../../core/directives/enter-submit.directive";
import {
  countInvalidFields,
  focusFirstInvalid,
} from "../../shared/utils/form-focus";
import { formatClientIdentifier } from "../../shared/utils/client-identifier";
import { getEmployeeNumber } from "../../model/interface/nurse";

@Component({
  selector: "app-checkin",
  standalone: true,
  imports: [
    DisableUnlessPermissionDirective,
    EnterSubmitDirective,
    FormsModule,
    CommonModule,
    TuiAppearance,
    TuiButton,
    TuiDataList,
    TuiDropdown,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiFiles,
    TuiFieldErrorPipe,
    TuiError,
    ReactiveFormsModule,
    AsyncPipe,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiTextareaModule,
    TuiInputPhoneModule,
    TuiComboBoxModule,
    TuiMultiSelectModule,
    TuiSelectModule,
    TuiDataListWrapper,
    TuiInputDateModule,
    TuiInputDateRangeModule,
    TuiTable,
    TuiStringifyContentPipe,
    TuiInputDateTimeModule,
    TuiInputTimeModule,
  ],
  templateUrl: "./checkin.component.html",
  styleUrl: "./checkin.component.scss",
})
export class CheckinComponent {
  @Input() inDialog = false;
  @Output() shiftCreated = new EventEmitter<any>();
  @Output() dialogCancel = new EventEmitter<void>();
  backLocation: string = "";
  /** Full list of clients fetched from server (after archive/active filter). */
  allClients: any[] = [];
  /**
   * Soft-warn flexibility: when off, the client picker only shows live
   * clients (active && !archived). Flipping it on widens the picker to
   * include inactive/archived clients - useful for backdating or
   * reactivation - and we render an inline warning when the chosen
   * client is in either of those states.
   */
  includeInactiveClients: boolean = false;
  /** Filtered list shown in the client combo box, scoped to selected clientType. */
  clients: any[] = [];
  nurses: any[] = [];
  nurseId: string = "";
  shifts: any[] = [];
  checkInForm: FormGroup;
  isLoading = false;
  isClientsLoading = false;
  nurse: any;
  isNationalHospital = false;
  isUnregisteredClient = false;
  /** True when the selected clientType is "Inward" - drives form layout and pre-fill behaviour. */
  isInwardClient = false;
  minDate: TuiDay = TuiDay.fromLocalNativeDate(new Date());

  // Patient Type filter options for the leading combo
  protected readonly clientTypeOptions: { label: string; value: string }[] = [
    { label: "Registered", value: "Registered" },
    { label: "Non Registered (With Home Nursing)", value: "Unregistered" },
    { label: "Inward", value: "Inward" },
  ];

  protected stringifyClientType(item: { label: string; value: string } | null): string {
    return item ? item.label : "";
  }
  beforeYear12hrNormalWithMeal: number = 0;
  beforeYear12hrNormalWithoutMeal: number = 0;
  beforeYear12hrBabycareWithMeal: number = 0;
  beforeYear12hrBabycareWithoutMeal: number = 0;
  beforeYear12hrNHPWithMeal: number = 0;
  beforeYear12hrNHPWithoutMeal: number = 0;
  beforeYear24hrNormalWithMeal: number = 0;
  beforeYear24hrBabycareWithMeal: number = 0;
  beforeYear24hrNHPWithMeal: number = 0;

  afterYear12hrNormalWithMeal: number = 0;
  afterYear12hrNormalWithoutMeal: number = 0;
  afterYear12hrBabycareWithMeal: number = 0;
  afterYear12hrBabycareWithoutMeal: number = 0;
  afterYear12hrNHPWithMeal: number = 0;
  afterYear12hrNHPWithoutMeal: number = 0;
  afterYear24hrNormalWithMeal: number = 0;
  afterYear24hrBabycareWithMeal: number = 0;
  afterYear24hrNHPWithMeal: number = 0;

  twelveHourNormalWithMeal: number = 0;
  twelveHourNormalWithoutMeal: number = 0;
  twelveHourBabycareWithMeal: number = 0;
  twelveHourBabycareWithoutMeal: number = 0;
  twelveHourNHPWithMeal: number = 0;
  twelveHourNHPWithoutMeal: number = 0;
  twentyFourHourNormalWithMeal: number = 0;
  twentyFourHourBabycareWithMeal: number = 0;
  twentyFourHourNHPWithMeal: number = 0;

  errorMessage: string | null = null;

  protected readonly rosterItems = [
    { items: "12 hours shift", value: 12 },
    { items: "24 hours shift", value: 24 },
  ];

  protected readonly rosterType = [
    { items: "Day Shift" },
    { items: "Night Shift" },
  ];

  protected readonly mealOptions = [
    { items: "With Meal" },
    { items: "Without Meal" },
  ];

  protected readonly typeOptions = [
    { items: "Normal" },
    { items: "Baby Care" },
    { items: "NHP (National Hospital)" },
  ];

  constructor(
    private clientService: ClientService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private alerts: TuiAlertService,
    private nurseService: NurseService,
    private salaryService: SalaryService,
    private shiftService: ShiftService,
    private chargesService: ChargesService,
    private salaryConfigService: SalaryConfigService,
    private chargeConfigService: ChargeConfigService
  ) {
    this.checkInForm = this.fb.group({
      clientType: [this.clientTypeOptions[0], Validators.required],
      client: [""],
      nurse: ["", Validators.required],
      address: [{ value: "", disabled: true }],
      mealOption: ["", Validators.required],
      typeOption: ["", Validators.required],
      roster: [null, Validators.required],
      rosterType: ["", Validators.required],
      startDateRange: [null, Validators.required],
      notes: [""],
      name: [""],
      mobile: [""],
      room: [""],
      bhtNumber: [""],
    });
  }

  ngOnInit(): void {
    this.checkInForm.valueChanges.subscribe(() => (this.errorMessage = null));
    this.loadClients();
    this.loadNurse();
    this.loadSalaryConfig();
    this.loadChargeConfig();

    this.route.queryParams.subscribe((params) => {
      this.backLocation = params["backLocation"];
    });

    this.checkInForm.get("typeOption")?.valueChanges.subscribe((value: any) => {
      // Guard: when the form is reset / cleared (e.g. switching into Inward
      // mode) `value` becomes "" or null - dereferencing `value.items` would
      // throw and put the form into a stuck state.
      if (!value) return;
      if (value.items === "NHP (National Hospital)") {
        this.checkInForm.get("address")?.setValue("NHP (National Hospital)");
        this.isNationalHospital = true;
        this.checkInForm.get("address")?.disable();
      } else if (value.items === "Normal" || value.items === "Baby Care") {
        this.checkInForm.get("address")?.enable();
        this.checkInForm.get("address")?.setValue("");
      }
    });

    this.checkInForm.get("roster")?.valueChanges.subscribe((value: any) => {
      if (value && value.items === "24 hours shift") {
        const withMealOption = this.mealOptions.find(
          (opt) => opt.items === "With Meal"
        );
        const withRosterType = this.rosterType.find(
          (opt) => opt.items === "Day Shift"
        );
        this.checkInForm.get("mealOption")?.setValue(withMealOption);
        this.checkInForm.get("mealOption")?.disable();
        this.checkInForm.get("rosterType")?.setValue(withRosterType);
        this.checkInForm.get("rosterType")?.disable();
      } else if (value && value.items === "12 hours shift") {
        this.checkInForm.get("mealOption")?.enable();
        this.checkInForm.get("rosterType")?.enable();
        // Don't clear existing selections here — for Inward clients we may
        // have just auto-filled mealOption from the client profile.
      }
    });

    this.checkInForm.get("client")?.valueChanges.subscribe((value: any) => {
      if (
        (value && value.registrationType == "Regular Client") ||
        this.isNationalHospital
      ) {
        this.isUnregisteredClient = true;
      } else {
        this.isUnregisteredClient = false;
      }

      // Inward client - pre-fill the shift fields from the client document.
      if (value && value.clientType === "Inward") {
        const preferredMeal = String(value.meal || "").trim().toLowerCase();
        const meal =
          preferredMeal
            ? this.mealOptions.find(
              (opt) => String(opt.items).trim().toLowerCase() === preferredMeal,
            )
            : null;
        const rosterValue = value.roster === "24 Hours" ? 24 : 12;
        const rosterItem = this.rosterItems.find((r) => r.value === rosterValue);
        const startDate = value.inwardDate
          ? TuiDay.fromLocalNativeDate(new Date(value.inwardDate))
          : null;

        this.checkInForm.patchValue({
          // Only overwrite when we can map the stored value. Otherwise keep any
          // existing selection/fallback that was set when switching clientType.
          mealOption: meal || this.checkInForm.get("mealOption")?.value || null,
          roster: rosterItem || null,
          room: value.roomNumber || "",
          bhtNumber: value.bhtNumber || "",
          name: value.name || "",
          mobile: value.guardianContactNo || "",
          startDate,
        });
      }
    });

    // React to client type changes - filter client list and toggle Inward layout.
    this.checkInForm.get("clientType")?.valueChanges.subscribe((value: any) => {
      this.applyClientTypeFilter(value?.value);
      // Reset client selection so users explicitly pick within the filtered list.
      this.checkInForm.patchValue({ client: "" }, { emitEvent: false });
    });
    // Apply initial filter (Registered by default).
    this.applyClientTypeFilter(this.checkInForm.get("clientType")?.value?.value);
  }

  /**
   * Filter the client combo box based on the selected client type, mirroring
   * the duty-roster eligibility rules: Registered, Unregistered+WithHomeNursing,
   * or Inward.
   */
  private applyClientTypeFilter(clientTypeValue: string | undefined): void {
    const type = clientTypeValue || "Registered";
    this.isInwardClient = type === "Inward";

    // Adjust validators - hide Type/Address for Inward; toggle as needed.
    const typeOption = this.checkInForm.get("typeOption");
    const address = this.checkInForm.get("address");
    const mealOption = this.checkInForm.get("mealOption");

    if (this.isInwardClient) {
      typeOption?.clearValidators();
      typeOption?.setValue("", { emitEvent: false });
      address?.disable({ emitEvent: false });
      address?.setValue("", { emitEvent: false });

      // Default mealOption to "With Meal" so admins are never blocked from
      // scheduling an Inward shift just because the linked client document
      // does not carry a `meal` value. The actual value is overwritten in the
      // client.valueChanges subscription below when the linked client has a
      // saved meal preference.
      if (!mealOption?.value) {
        const fallback = this.mealOptions.find((m) => m.items === "With Meal");
        if (fallback) {
          mealOption?.setValue(fallback, { emitEvent: false });
        }
      }
    } else {
      typeOption?.setValidators(Validators.required);
    }
    typeOption?.updateValueAndValidity({ emitEvent: false });

    this.clients = this.allClients.filter((c: any) => {
      const ct = c?.clientType || "Registered";
      if (type === "Registered") return ct === "Registered";
      if (type === "Unregistered") {
        return ct === "Unregistered" && c?.nursingType === "With Home Nursing";
      }
      if (type === "Inward") return ct === "Inward";
      return true;
    });
  }

  updateConditionalValidators() {
    // Room / BHT Number are Inward-only in the template now (they were
    // previously also surfaced for Unregistered / NHP shifts but those
    // are not hospital admissions and shouldn't carry these fields).
    // name / mobile stay tied to Unregistered / NHP as before.
    const fields = ["name", "mobile"];

    fields.forEach((field) => {
      const control = this.checkInForm.get(field);
      if (this.isUnregisteredClient) {
        control?.setValidators(Validators.required);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    });
  }

  loadSalaryConfig(): void {
    this.salaryConfigService.getAllSalaryConfig().subscribe({
      next: (response: any) => {
        const salaryConfig = response.data[0];
        if (salaryConfig) {
          this.beforeYear12hrNormalWithMeal =
            salaryConfig.beforeYear12hrNormalWithMeal;
          this.beforeYear12hrNormalWithoutMeal =
            salaryConfig.beforeYear12hrNormalWithoutMeal;
          this.beforeYear12hrBabycareWithMeal =
            salaryConfig.beforeYear12hrBabycareWithMeal;
          this.beforeYear12hrBabycareWithoutMeal =
            salaryConfig.beforeYear12hrBabycareWithoutMeal;
          this.beforeYear12hrNHPWithMeal =
            salaryConfig.beforeYear12hrNHPWithMeal;
          this.beforeYear12hrNHPWithoutMeal =
            salaryConfig.beforeYear12hrNHPWithoutMeal;
          this.beforeYear24hrNormalWithMeal =
            salaryConfig.beforeYear24hrNormalWithMeal;
          this.beforeYear24hrBabycareWithMeal =
            salaryConfig.beforeYear24hrBabycareWithMeal;
          this.beforeYear24hrNHPWithMeal =
            salaryConfig.beforeYear24hrNHPWithMeal;

          this.afterYear12hrNormalWithMeal =
            salaryConfig.afterYear12hrNormalWithMeal;
          this.afterYear12hrNormalWithoutMeal =
            salaryConfig.afterYear12hrNormalWithoutMeal;
          this.afterYear12hrBabycareWithMeal =
            salaryConfig.afterYear12hrBabycareWithMeal;
          this.afterYear12hrBabycareWithoutMeal =
            salaryConfig.afterYear12hrBabycareWithoutMeal;
          this.afterYear12hrNHPWithMeal = salaryConfig.afterYear12hrNHPWithMeal;
          this.afterYear12hrNHPWithoutMeal =
            salaryConfig.afterYear12hrNHPWithoutMeal;
          this.afterYear24hrNormalWithMeal =
            salaryConfig.afterYear24hrNormalWithMeal;
          this.afterYear24hrBabycareWithMeal =
            salaryConfig.afterYear24hrBabycareWithMeal;
          this.afterYear24hrNHPWithMeal = salaryConfig.afterYear24hrNHPWithMeal;
        }
      },
      error: (error) => {
        console.error("Error fetching salary config:", error);
      },
    });
  }

  loadChargeConfig(): void {
    this.chargeConfigService.getAllChargeConfig().subscribe({
      next: (response: any) => {
        const chargeConfig = response.data[0];
        if (chargeConfig) {
          this.twelveHourNormalWithMeal = chargeConfig.twelveHourNormalWithMeal;
          this.twelveHourNormalWithoutMeal =
            chargeConfig.twelveHourNormalWithoutMeal;
          this.twelveHourBabycareWithMeal =
            chargeConfig.twelveHourBabycareWithMeal;
          this.twelveHourBabycareWithoutMeal =
            chargeConfig.twelveHourBabycareWithoutMeal;
          this.twelveHourNHPWithMeal = chargeConfig.twelveHourNHPWithMeal;
          this.twelveHourNHPWithoutMeal = chargeConfig.twelveHourNHPWithoutMeal;
          this.twentyFourHourNormalWithMeal =
            chargeConfig.twentyFourHourNormalWithMeal;
          this.twentyFourHourBabycareWithMeal =
            chargeConfig.twentyFourHourBabycareWithMeal;
          this.twentyFourHourNHPWithMeal =
            chargeConfig.twentyFourHourNHPWithMeal;
        }
      },
      error: (error) => {
        console.error("Error fetching charge config:", error);
      },
    });
  }

  loadClients(): void {
    this.isClientsLoading = true;
    this.clientService.getAllClients().subscribe({
      next: (response: any) => {
        const clients = Array.isArray(response) ? response : [];
        // When the toggle is off (default) we hide archived/inactive
        // clients to keep the picker tidy. When the user flips it on we
        // include them so they can backdate or reactivate.
        this.allClients = this.includeInactiveClients
          ? clients
          : clients.filter(
            (c: any) => !c?.archived && c?.active !== false,
          );
        this.applyClientTypeFilter(this.checkInForm.get("clientType")?.value?.value);
        this.isClientsLoading = false;
      },
      error: (error) => {
        console.error("Error fetching clients:", error);
        this.isClientsLoading = false;
      },
    });
  }

  toggleIncludeInactiveClients(value: boolean): void {
    this.includeInactiveClients = value;
    this.loadClients();
  }

  /**
   * True when the currently selected client is archived or inactive.
   * Used by the template to surface a soft-warn banner.
   */
  isSelectedClientInactive(): boolean {
    const ctrl = this.checkInForm.get("client");
    if (!ctrl?.value) return false;
    const id =
      typeof ctrl.value === "object" ? ctrl.value._id : ctrl.value;
    const client = this.allClients.find((c: any) => c._id === id);
    if (!client) return false;
    return Boolean(client.archived) || client.active === false;
  }

  loadNurse(): void {
    this.nurseService.getAllNurses().subscribe({
      next: (response: any) => {
        const nurses = Array.isArray(response) ? response : [];
        this.nurses = nurses.filter((n: any) => !n?.archived && n?.active !== false);
      },
      error: (error) => {
        console.error("Error fetching clients:", error);
      },
    });
  }

  /**
   * Display format for the Select Client combo:
   *   `REG-0001 - Client Name (Patient Name)`
   * Falls back gracefully when either client or patient name is
   * missing so we never show stray separators in the dropdown.
   */
  stringifyClients = (client: any): string => {
    const formatOne = (c: any): string => {
      const id = formatClientIdentifier(c);
      const name = String(c?.name || "").trim();
      const guardian = String(c?.guardianName || "").trim();
      if (guardian && name) return `${id} - ${guardian} (${name})`;
      if (guardian) return `${id} - ${guardian}`;
      if (name) return `${id} - ${name}`;
      return id;
    };

    if (!client) return "";
    if (typeof client === "object") {
      return formatOne(client);
    }
    const found = this.allClients.find((c: any) => c._id === client);
    if (!found) return "";
    return formatOne(found);
  };

  stringifyNurses = (nurse: any): string => {
    if (!nurse) return "";
    if (typeof nurse === "object") {
      return `${getEmployeeNumber(nurse)} - ${nurse.name}`;
    }
    const foundNurse = this.nurses.find((c: any) => c._id === nurse);
    return foundNurse ? `${getEmployeeNumber(foundNurse)} - ${foundNurse.name}` : "";
  };

  clientSearch = "";
  nurseSearch = "";

  get filteredClients(): any[] {
    const searchVal = (this.clientSearch || "").trim().toLowerCase();
    if (!searchVal) {
      return this.clients;
    }
    return this.clients.filter((client) =>
      this.stringifyClients(client).toLowerCase().includes(searchVal)
    );
  }

  get filteredNurses(): any[] {
    const searchVal = (this.nurseSearch || "").trim().toLowerCase();
    if (!searchVal) {
      return this.nurses;
    }
    return this.nurses.filter((nurse) =>
      this.stringifyNurses(nurse).toLowerCase().includes(searchVal)
    );
  }

  stringifyDropdown(item: { items: string; value: number } | null): string {
    if (!item) return "";
    return item.items;
  }

  checkIn(): void {
    this.errorMessage = null;
    this.updateConditionalValidators();
    if (this.checkInForm.invalid) {
      const count = countInvalidFields(this.checkInForm);
      focusFirstInvalid(this.checkInForm);
      const msg = `${count} field${count === 1 ? "" : "s"} need attention before saving.`;
      this.errorMessage = msg;
      this.alerts
        .open(
          msg,
          { label: "Form incomplete", appearance: "warning" },
        )
        .subscribe();
      return;
    }

    const selectedNurse = this.checkInForm.get("nurse")?.value;
    const selectedNurseId = selectedNurse._id;
    const range = this.checkInForm.get("startDateRange")?.value as TuiDayRange;
    if (!range) return;
    const dateValue = range.from;
    const isBulk = !(range.from.day === range.to.day && range.from.month === range.to.month && range.from.year === range.to.year);
    const startDateNative = dateValue.toLocalNativeDate();
    const startDateTime = new Date(startDateNative.getTime());
    const shiftMonth = startDateTime.getMonth() + 1;
    const shiftYear = startDateTime.getFullYear();
    const lastDayOfShiftMonth = new Date(
      shiftYear,
      startDateTime.getMonth() + 1,
      0
    ).getDate();
    const isLastDayOfMonth = startDateTime.getDate() === lastDayOfShiftMonth;
    const status = isLastDayOfMonth ? "Completed" : "Ongoing";
    const roster = this.checkInForm.get("roster")?.value;
    const rosterType = this.checkInForm.get("rosterType")?.value;
    const rosterHours = roster.value;
    // Inward shifts skip the type selector - default to "Normal" so existing
    // salary/charge calculations and shift.shiftType enum still apply.
    const typeOption =
      this.checkInForm.get("typeOption")?.value ||
      (this.isInwardClient
        ? this.typeOptions.find((opt) => opt.items === "Normal")
        : null);
    const mealOption = this.checkInForm.get("mealOption")?.value;

    // ----------------------------
    // 1) CALCULATE NURSE SALARY
    // ----------------------------
    let shiftSalary = 0;
    const experience = selectedNurse.experienceLevel;

    if (experience <= 6) {
      // Use beforeYear config values
      if (roster.value === 12) {
        if (typeOption.items === "Normal") {
          shiftSalary =
            mealOption.items === "With Meal"
              ? this.beforeYear12hrNormalWithMeal
              : this.beforeYear12hrNormalWithoutMeal;
        } else if (typeOption.items === "Baby Care") {
          shiftSalary =
            mealOption.items === "With Meal"
              ? this.beforeYear12hrBabycareWithMeal
              : this.beforeYear12hrBabycareWithoutMeal;
        } else if (typeOption.items === "NHP (National Hospital)") {
          shiftSalary =
            mealOption.items === "With Meal"
              ? this.beforeYear12hrNHPWithMeal
              : this.beforeYear12hrNHPWithoutMeal;
        }
      } else if (roster.value === 24) {
        if (typeOption.items === "Normal") {
          shiftSalary = this.beforeYear24hrNormalWithMeal;
        } else if (typeOption.items === "Baby Care") {
          shiftSalary = this.beforeYear24hrBabycareWithMeal;
        } else if (typeOption.items === "NHP (National Hospital)") {
          shiftSalary = this.beforeYear24hrNHPWithMeal;
        }
      }
    } else {
      // Use afterYear config values
      if (roster.value === 12) {
        if (typeOption.items === "Normal") {
          shiftSalary =
            mealOption.items === "With Meal"
              ? this.afterYear12hrNormalWithMeal
              : this.afterYear12hrNormalWithoutMeal;
        } else if (typeOption.items === "Baby Care") {
          shiftSalary =
            mealOption.items === "With Meal"
              ? this.afterYear12hrBabycareWithMeal
              : this.afterYear12hrBabycareWithoutMeal;
        } else if (typeOption.items === "NHP (National Hospital)") {
          shiftSalary =
            mealOption.items === "With Meal"
              ? this.afterYear12hrNHPWithMeal
              : this.afterYear12hrNHPWithoutMeal;
        }
      } else if (roster.value === 24) {
        if (typeOption.items === "Normal") {
          shiftSalary = this.afterYear24hrNormalWithMeal;
        } else if (typeOption.items === "Baby Care") {
          shiftSalary = this.afterYear24hrBabycareWithMeal;
        } else if (typeOption.items === "NHP (National Hospital)") {
          shiftSalary = this.afterYear24hrNHPWithMeal;
        }
      }
    }

    // ----------------------------
    // 2) CALCULATE CLIENT CHARGES
    // ----------------------------
    const selectedClient = this.clients.find(
      (client) => client._id === this.checkInForm.get("client")?.value._id
    );
    let shiftCharges = 0;
    if (roster.value === 12) {
      if (typeOption.items === "Normal") {
        shiftCharges =
          mealOption.items === "With Meal"
            ? this.twelveHourNormalWithMeal
            : this.twelveHourNormalWithoutMeal;
      } else if (typeOption.items === "Baby Care") {
        shiftCharges =
          mealOption.items === "With Meal"
            ? this.twelveHourBabycareWithMeal
            : this.twelveHourBabycareWithoutMeal;
      } else if (typeOption.items === "NHP (National Hospital)") {
        shiftCharges =
          mealOption.items === "With Meal"
            ? this.twelveHourNHPWithMeal
            : this.twelveHourNHPWithoutMeal;
      }
    } else if (roster.value === 24) {
      if (typeOption.items === "Normal") {
        shiftCharges = this.twentyFourHourNormalWithMeal;
      } else if (typeOption.items === "Baby Care") {
        shiftCharges = this.twentyFourHourBabycareWithMeal;
      } else if (typeOption.items === "NHP (National Hospital)") {
        shiftCharges = this.twentyFourHourNHPWithMeal;
      }
    }

    // ----------------------------
    // 3) CREATE SHIFT OBJECT
    // ----------------------------
    const shiftDateStr = range.from.toJSON();
    const newShift: any = {
      nurseID: selectedNurseId,
      clientID: this.checkInForm.get("client")?.value._id,
      date: shiftDateStr,
      rooster: rosterHours,
      roosterType: rosterType.items,
      salary: shiftSalary,
      charges: shiftCharges,
      address: this.checkInForm.get("address")?.value,
      mealOption: mealOption.items,
      shiftType: typeOption.items,
      notes: this.checkInForm.get("notes")?.value,
      availabilityUpdated: false,
      name: this.checkInForm.get("name")?.value,
      mobile: this.checkInForm.get("mobile")?.value,
      room: this.checkInForm.get("room")?.value,
      bhtNumber: this.checkInForm.get("bhtNumber")?.value,
      isUnregisteredPatient: this.isUnregisteredClient,
    };

    this.isLoading = true;

    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    const startNative = range.from.toLocalNativeDate();
    startNative.setHours(0, 0, 0, 0);
    const isShiftTodayOrPast = startNative <= todayMidnight;

    const nurseUpdateObservable = (isShiftTodayOrPast && selectedNurseId)
      ? this.nurseService.updateNurse({ _id: selectedNurseId, availability: false }).pipe(
          catchError(() => of({ success: true })) // non-blocking for shift creation
        )
      : of({ success: true });

    if (selectedNurseId) {
      if (isBulk) {
        const bulkPayload = {
          ...newShift,
          startDate: range.from.toJSON(),
          endDate: range.to.toJSON()
        };
        delete bulkPayload.date;

        nurseUpdateObservable
          .pipe(
            switchMap(() => {
              return this.shiftService.createBulkShifts(bulkPayload);
            }),
            catchError((error) => {
              this.isLoading = false;
              const msg = error.error?.message || "Failed to process bulk shifts";
              this.errorMessage = msg;
              this.alerts
                .open(msg, {
                  label: "Error",
                  appearance: "negative",
                })
                .subscribe();
              return of(null);
            }),
            switchMap((bulkRes: any) => {
              this.isLoading = false;
              if (!bulkRes || !bulkRes.data) return of(null);
              
              const { scheduledDates, createdCount } = bulkRes.data;
              
              let alertMsg = `Bulk schedule completed. Scheduled ${createdCount} shifts.`;
              if (scheduledDates && scheduledDates.length > 0) {
                alertMsg += `\nScheduled dates: ${scheduledDates.join(", ")}`;
              }

              this.alerts
                .open(alertMsg, {
                  label: "Success",
                  appearance: "success",
                  autoClose: 10000
                })
                .subscribe();

              if (this.inDialog) {
                const formattedDates = (scheduledDates || []).map((d: string) => d.split("T")[0]);
                this.shiftCreated.emit({
                  clientID: newShift.clientID,
                  dates: formattedDates
                });
              } else {
                if (this.backLocation) {
                  this.router.navigate([this.backLocation], {});
                } else {
                  this.router.navigate(["admin/nurse"], {});
                }
              }

              if (isShiftTodayOrPast) {
                selectedNurse.availability = false;
              }
              return of(bulkRes.data);
            })
          )
          .subscribe(
            (finalRes: any) => {
              if (finalRes) {
                console.log("Bulk shifts processed successfully:", finalRes);
              }
            },
            (error) => {
              this.isLoading = false;
              console.error("Error in final subscription:", error);
            }
          );
        return;
      }

      nurseUpdateObservable
        .pipe(
          switchMap(() => {
            return this.shiftService.createShift(newShift);
          }),
          catchError((error) => {
            this.isLoading = false;
            const msg = error.error?.message || "Failed to schedule shift";
            this.errorMessage = msg;
            this.alerts
              .open(msg, {
                label: "Error",
                appearance: "negative",
              })
              .subscribe();
            return of(null);
          }),
          switchMap((shiftRes: any) => {
            this.isLoading = false;
            if (!shiftRes || !shiftRes.data) return of(null);
            const createdShift = shiftRes.data;

            this.alerts
              .open("Shift created successfully", {
                label: "Success",
                appearance: "success",
              })
              .subscribe();

            if (this.inDialog) {
              this.shiftCreated.emit({
                clientID: newShift.clientID,
                dates: [shiftDateStr]
              });
            } else {
              if (this.backLocation) {
                this.router.navigate([this.backLocation], {});
              } else {
                this.router.navigate(["admin/nurse"], {});
              }
            }

            this.shifts.push(createdShift);
            selectedNurse.salary = shiftSalary;
            if (isShiftTodayOrPast) {
              selectedNurse.availability = false;
            }

            return of(createdShift);
          })
        )
        .subscribe(
          (finalRes: any) => {
            if (finalRes) {
              console.log("Shift created successfully:", finalRes);
            }
          },
          (error) => {
            this.isLoading = false;
            console.error("Error in final subscription:", error);
          }
        );
    }
  }

  cancelCheckin(): void {
    if (this.inDialog) {
      this.dialogCancel.emit();
    } else {
      this.router.navigate(["/admin/nurse"]);
    }
  }
}
